import type { SortColumn, SortDirection } from "../hooks/useExplorerUrlState";

export interface Sortable {
  name: string;
  lastModified: string | null;
  size: number | null;
  kind: "folder" | "file";
}

/** Folder rows first (ADR 0010), then files, each group sorted independently by the active column. */
export function sortRows<T extends Sortable>(rows: T[], sort: SortColumn, dir: SortDirection): T[] {
  const factor = dir === "asc" ? 1 : -1;
  function compare(a: T, b: T): number {
    if (sort === "name") {
      return factor * a.name.localeCompare(b.name);
    }
    if (sort === "lastModified") {
      return factor * (a.lastModified ?? "").localeCompare(b.lastModified ?? "");
    }
    return factor * ((a.size ?? -1) - (b.size ?? -1));
  }
  const folders = rows.filter((row) => row.kind === "folder").sort(compare);
  const files = rows.filter((row) => row.kind === "file").sort(compare);
  return [...folders, ...files];
}

/** Client-side, case-insensitive substring match on the name, within the currently displayed folder or root listing. */
export function filterRowsByName<T extends { name: string }>(rows: T[], search: string): T[] {
  const needle = search.trim().toLowerCase();
  if (!needle) {
    return rows;
  }
  return rows.filter((row) => row.name.toLowerCase().includes(needle));
}

/** The most recent lastModified among a folder's files, for the root listing's "Last modified" column; null for an empty folder. */
export function mostRecent(dates: string[]): string | null {
  return dates.length === 0 ? null : dates.reduce((latest, current) => (current > latest ? current : latest));
}
